import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { useHistory } from "react-router";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { Price } from "@syos-music/common";
import {
  AddressField,
  SwitchField,
  QuantityField,
  TextField,
  SelectField,
  CheckboxField,
} from "@syos-music/form";
import { Product } from "@syos-music/products";
import { FormattedNumber } from "react-intl";

import {
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  Container,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Delete as DeleteIcon, Plus as AddIcon } from "mdi-material-ui";

import Layout from "components/common/Layout";
import ProductSelectField from "components/form/ProductSelectField";
import ShippingMethodSelectField from "components/form/ShippingMethodSelectField";

const PRODUCT_FRAGMENT = gql`
  fragment CreateInvoicePage_product on Product {
    id
    account
    prices {
      currency
      value
    }
  }
`;
const QUERY = gql`
  query {
    countries {
      id
      taxRate
    }
  }
`;

const MUTATION = gql`
  mutation (
    $locale: Locale!
    $shippingAddress: AddressInput!
    $billingAddress: AddressInput!
    $rows: [InvoiceRowInput]!
    $subTotal: PriceInput!
    $shippingFee: PriceInput!
    $taxRate: Float!
    $taxTotal: PriceInput!
    $total: PriceInput!
    $isB2B: Boolean!
    $additionalMentions: [String]!
    $paymentProvider: PaymentProvider
    $paymentId: String
  ) {
    CreateInvoice(
      locale: $locale

      shippingAddress: $shippingAddress
      billingAddress: $billingAddress

      rows: $rows
      subTotal: $subTotal
      shippingFee: $shippingFee
      taxRate: $taxRate
      taxTotal: $taxTotal
      total: $total

      isB2B: $isB2B
      additionalMentions: $additionalMentions

      paymentProvider: $paymentProvider
      paymentId: $paymentId
    ) {
      invoice {
        id
        reference
        file {
          id
          url
        }
      }
    }
  }
`;

export default function InvoiceForm() {
  const history = useHistory();

  const [createInvoice] = useMutation(MUTATION);

  const { data, loading } = useQuery(QUERY);
  if (loading) return <Layout.Loading />;

  return (
    <Container fixed sx={{ p: 2 }}>
      <Layout.Toolbar fixed>
        <Typography variant="h4" color="primary">
          Création d’une facture
        </Typography>
      </Layout.Toolbar>

      <Form
        mutators={{ ...arrayMutators }}
        onSubmit={async (values) => {
          const rows = values.rows.map((row) => {
            const unitPrice = row.product.prices.find(
              (price) => price.currency === values.currency
            );

            return {
              description: row.product.label,
              reference: row.product.id,
              unitPrice,
              quantity: row.quantity,
              total: {
                currency: values.currency,
                value: unitPrice.value * row.quantity,
              },
              accountingCode: row.product.account,
            };
          });

          const subTotal = {
            currency: values.currency,
            value: rows.reduce((acc, row) => acc + row.total.value, 0),
          };

          const shippingFee = values.shippingMethod?.finalPrice;
          const shippingCountry = data?.countries.find(
            ({ id }) => id === values.shippingAddress?.countryId
          );
          const taxRate = shippingCountry?.taxRate;
          const taxTotal = {
            currency: values.currency,
            value: (subTotal.value + shippingFee.value) * taxRate,
          };
          const total = {
            currency: values.currency,
            value: subTotal.value + shippingFee.value + taxTotal.value,
          };

          const variables = {
            locale: values.locale,

            shippingAddress: values.shippingAddress,
            billingAddress: values.useBillingAddress
              ? values.billingAddress
              : values.shippingAddress,

            rows,
            subTotal,
            shippingFee,
            taxRate,
            taxTotal,
            total,

            isB2B: values.isB2B,
            additionalMentions: [values.additionalMention],

            paymentProvider: values.paymentProvider,
            paymentId: values.paymentId,
          };

          return createInvoice({ variables })
            .then(({ data }) => {
              Object.assign(document.createElement("a"), {
                href: data.CreateInvoice.invoice.file.url,
                target: "_blank",
                rel: "noopener noreferrer",
              }).click();

              history.push("/billing");
            })
            .catch(() => true);
        }}
        initialValues={{
          currency: "EUR",
          locale: "fr",
          useBillingAddress: false,
          rows: [],
          isB2B: false,
        }}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, values, submitting }) => {
          const subTotal = {
            currency: values.currency,
            value: values.rows.reduce((acc, row) => {
              const productPrice = row.product?.prices.find(
                (price) => price.currency === values.currency
              );
              return acc + productPrice?.value * row.quantity;
            }, 0),
          };

          const shippingFee = values.shippingMethod?.finalPrice ?? {
            currency: values.currency,
            value: NaN,
          };

          const shippingCountry = data?.countries.find(
            ({ id }) => id === values.shippingAddress?.countryId
          );

          const taxRate = shippingCountry?.taxRate;
          const taxTotal = {
            currency: values.currency,
            value: (subTotal.value + shippingFee.value) * taxRate,
          };
          const total = {
            currency: values.currency,
            value: subTotal.value + shippingFee.value + taxTotal.value,
          };

          return (
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <Grid container>
                <Grid item xs={12}>
                  <SelectField name="locale" label="Langue" required>
                    <MenuItem value="fr">Français</MenuItem>
                    <MenuItem value="en">Anglais</MenuItem>
                  </SelectField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="h3" color="primary" gutterBottom>
                    Adresse de livraison
                  </Typography>

                  <AddressField
                    name="shippingAddress"
                    withVatNumber
                    withPhone
                    fieldsProps={{
                      countryId: { required: true },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SwitchField
                    name="useBillingAddress"
                    label={
                      <Typography variant="h3" color="primary">
                        Adresse de facturation
                      </Typography>
                    }
                    sx={{ mt: "-3px" }}
                  />

                  {values.useBillingAddress && (
                    <AddressField
                      name="billingAddress"
                      withVatNumber
                      withPhone
                    />
                  )}
                </Grid>

                <Grid item xs>
                  <Typography variant="h3" color="primary" gutterBottom>
                    Contenu
                  </Typography>
                </Grid>

                <Grid item>
                  <SelectField name="currency" required>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                  </SelectField>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer>
                    <Table
                      sx={{
                        "& th:nth-of-type(1)": { minWidth: 38 },
                        "& th:nth-of-type(2)": { width: "100%" },
                        "& td": {
                          whiteSpace: "nowrap",
                          "&:nth-of-type(2)": { width: "50%" },
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell>Produit</TableCell>
                          <TableCell align="center">TVA</TableCell>
                          <TableCell align="center">P.U. HT</TableCell>
                          <TableCell align="center">Qté</TableCell>
                          <TableCell align="center">Total HT</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <FieldArray name="rows">
                          {({ fields }) => (
                            <>
                              {fields.map((fieldName, idx) => {
                                const { product, quantity } = fields.value[idx];

                                return (
                                  <TableRow key={fieldName}>
                                    <TableCell padding="checkbox">
                                      <IconButton
                                        onClick={() => fields.remove(idx)}
                                        size="small"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>

                                    <TableCell>
                                      <ProductSelectField
                                        name={`${fieldName}.product`}
                                        required
                                        priceTaxRate={taxRate}
                                        fragment={PRODUCT_FRAGMENT}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      <FormattedNumber
                                        value={taxRate}
                                        // eslint-disable-next-line react/style-prop-object
                                        style="percent"
                                        minimumFractionDigits={1}
                                        maximumFractionDigits={1}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      {product && (
                                        <Product.Price
                                          product={product}
                                          currency={values.currency}
                                        />
                                      )}
                                    </TableCell>

                                    <TableCell align="center">
                                      <QuantityField
                                        name={`${fieldName}.quantity`}
                                        fullWidth={false}
                                      />
                                    </TableCell>

                                    <TableCell align="right">
                                      {product && (
                                        <Product.Price
                                          product={product}
                                          quantity={quantity}
                                          currency={values.currency}
                                        />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              <TableRow>
                                <TableCell colSpan={6} align="right">
                                  <Button
                                    onClick={() =>
                                      fields.push({
                                        unitPrice: {
                                          currency: values.currency,
                                        },
                                        quantity: 1,
                                      })
                                    }
                                    startIcon={<AddIcon />}
                                    size="small"
                                    variant="text"
                                  >
                                    Ajouter une ligne
                                  </Button>
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        </FieldArray>

                        <TableRow>
                          <TableCell
                            rowSpan={5}
                            colSpan={2}
                            sx={{ borderBottomColor: "transparent" }}
                          />
                          <TableCell colSpan={3}>Sous-total</TableCell>
                          <TableCell align="right">
                            <Price {...subTotal} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            <ShippingMethodSelectField
                              name="shippingMethod"
                              priceCountryId={shippingCountry?.id}
                              cartTotal={subTotal}
                              priceTaxRate={taxRate}
                              required
                              label="Frais de livraison"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Price {...shippingFee} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={3}>
                            TVA&nbsp;
                            <FormattedNumber
                              value={taxRate}
                              // eslint-disable-next-line react/style-prop-object
                              style="percent"
                              minimumFractionDigits={1}
                              maximumFractionDigits={1}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Price {...taxTotal} />
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{ borderBottomColor: "transparent" }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ borderBottomColor: "transparent" }}
                          >
                            <Price {...total} />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <CheckboxField
                    name="isB2B"
                    label="Cette facture s’adresse à un professionnel"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="additionalMention"
                    multiline
                    placeholder="Mentions supplémentaires"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SelectField
                    name="paymentProvider"
                    label="Moyen de paiement"
                    clearable
                  >
                    <MenuItem value="NONE">Aucun</MenuItem>
                    <MenuItem value="STRIPE">Stripe</MenuItem>
                    <MenuItem value="PAYPAL">Paypal</MenuItem>
                    <MenuItem value="AMAZON">Amazon</MenuItem>
                    <MenuItem value="SUMUP">SumUp</MenuItem>
                    <MenuItem value="TRANSFER">Virement</MenuItem>
                    <MenuItem value="CHECK">Chèque</MenuItem>
                    <MenuItem value="CASH">Liquide</MenuItem>
                  </SelectField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="paymentId"
                    label="Identifiant de paiement"
                    clearable
                  />
                </Grid>

                <Grid item>
                  <LoadingButton type="submit" loading={submitting}>
                    Créer
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Form>
    </Container>
  );
}
